import {
  cardPlanCatalogResponseSchema,
  type CardPlanCatalogResponse,
  type PaidPlanUppercase
} from '@inktap/contracts/customer-commerce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { request } from './api';

export type CardPlan = PaidPlanUppercase;
export type CardPlanOffer = CardPlanCatalogResponse['items'][number];
export type CardPlanCatalog = CardPlanCatalogResponse;

function reviewedTerms(catalog: CardPlanCatalog, plan: CardPlan) {
  const offer = catalog.items.find((item) => item.plan === plan);
  return JSON.stringify(catalog.existingPlan ? { existingPlan: catalog.existingPlan } : {
    existingPlan: null, trialDays: catalog.trialDays, plan, pricingToken: offer?.pricingToken,
    unitAmount: offer?.unitAmount, currency: offer?.currency, interval: offer?.interval,
    intervalCount: offer?.intervalCount
  });
}

// This is review state, not purchase authority. Checkout independently revalidates
// the current plan, Stripe Price and existing subscription on the server.
export function useCardPlanCheckout(active: boolean, refreshKey: string) {
  const [catalog, setCatalog] = useState<CardPlanCatalog | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<CardPlan>('BASIC');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const refresh = useCallback(async () => {
    const version = ++sequence.current;
    setLoading(true);
    setCatalog(null);
    setError(null);
    try {
      const payload = await request<unknown>('/billing/card-plan-catalog', { cache: 'no-store' });
      if (version !== sequence.current) return null;
      const response = cardPlanCatalogResponseSchema.safeParse(payload);
      if (!response.success) throw new Error('Subscription terms are unavailable.');
      setCatalog(response.data);
      return response.data;
    } catch {
      if (version === sequence.current) setError('Unable to confirm subscription terms. Refresh before continuing.');
      return null;
    } finally {
      if (version === sequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    const reload = () => { void refresh(); };
    const visible = () => { if (document.visibilityState === 'visible') reload(); };
    reload();
    window.addEventListener('focus', reload);
    window.addEventListener('pageshow', reload);
    document.addEventListener('visibilitychange', visible);
    return () => {
      ++sequence.current;
      window.removeEventListener('focus', reload);
      window.removeEventListener('pageshow', reload);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [active, refreshKey, refresh]);

  const reviewCurrent = useCallback(async () => {
    const shown = catalog;
    const current = await refresh();
    if (!current || !shown || (!current.existingPlan && !current.items.some((item) => item.plan === selectedPlan))) {
      throw new Error('Unable to confirm subscription terms. Review the available plans before continuing.');
    }
    if (reviewedTerms(shown, selectedPlan) !== reviewedTerms(current, selectedPlan)) {
      throw new Error('Your subscription terms changed. Review the refreshed plan before continuing.');
    }
    const offer = current.items.find((item) => item.plan === selectedPlan);
    return current.existingPlan
      ? { path: '/billing/card-order-session', fields: {} }
      : { path: '/billing/bundle-checkout-session', fields: {
        plan: selectedPlan.toLowerCase(), ...(offer?.pricingToken ? { pricingToken: offer.pricingToken } : {})
      } };
  }, [catalog, refresh, selectedPlan]);

  return { catalog, selectedPlan, setSelectedPlan, loading, error, refresh, reviewCurrent,
    unavailable: loading || !catalog || (!catalog.existingPlan && !catalog.items.some((item) => item.plan === selectedPlan)) };
}
